/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth/atoms'
import { ApiError, apiErrorEvent, apiRequest } from '@root/shared/api'
import { tradeApiClientAtom } from '@root/shared/api/trade-api-client-atom'
import { reatomResource, SET_NOT_INITIALIZED } from '@root/shared/lib'

import type { AccessListData } from '@blade/trade-api-client'

/**
 * Whether the signed-in account is let into the terminal.
 *
 * The terminal is invite-only. The server refuses every route to an account
 * that is not in yet, whatever this says; this only decides which screen to
 * show — the app, or the queue — so a waiting person is not greeted by an app
 * full of errors.
 */

export type TerminalAccess = AccessListData

export const terminalAccessStateAtom = reatomResource(
  async (ctx) => {
    // Nobody signed in has no place to be told about; the gate sends them
    // to sign in instead.
    if (!ctx.spy(isLoggedInAtom)) {
      return SET_NOT_INITIALIZED
    }

    const { tradeApiClient } = ctx.get(tradeApiClientAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.access.accessList, []),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  { name: 'terminalAccessStateAtom', retry: { enabled: true } },
)

/** Let in, not yet, or not known yet. */
export const terminalAccessGrantedAtom = atom(
  (ctx) =>
    pipe(
      ctx.spy(terminalAccessStateAtom),
      AsyncStateN.map((it) => it.granted),
      AsyncStateN.getOrElseW(() => undefined),
    ),
  'terminalAccessGrantedAtom',
)

export const refreshTerminalAccessAction = action(async (ctx) => {
  await ctx.schedule(() => terminalAccessStateAtom.refresh(ctx))
}, 'refreshTerminalAccessAction')

/** Whether this page has shown the queue; set once, never cleared. */
let wasWaiting = false

// Let in while the queue screen was up: start the app over. Everything it
// loaded meanwhile — the trading mode, wallets, balances, settings — was
// refused, and a fresh start is the one way to be sure none of those
// refusals is left standing, such as Basic showing the wallet interface
// because its mode was never read. Only on that change: an account that
// was already in when the page opened loads once, as always.
terminalAccessGrantedAtom.onChange((_ctx, granted) => {
  if (granted === true && wasWaiting) {
    location.reload()
  }
  if (granted === false) {
    wasWaiting = true
  }
})

// Access taken away mid-session: the next refused request says so. Only
// while the account is believed to be in — a waiting account gets these for
// every background request, and re-asking on each would be a flood.
apiErrorEvent.onCall((ctx, error) => {
  if (
    error.code === 'ACCESS_PENDING' &&
    ctx.get(terminalAccessGrantedAtom) === true
  ) {
    void refreshTerminalAccessAction(ctx)
  }
})
